import {
  DialogAction,
  DialogComponent,
  ManagedDialogProps,
} from 'Subsystems/dialog/dialogTypes.ts';
import { ComponentProps } from 'react';

// Defines the component's own custom props, without the managed ones.
type CustomDialogProps<C extends DialogComponent<any>> = Omit<
  ComponentProps<C>, // every prop
  keyof ManagedDialogProps // minus the managed ones.
>;
// Defines the props that the developer can supply and no more.
type SuppliedDialogProps<
  C extends DialogComponent<any>, // dialog component
  P extends CustomDialogProps<C>, // custom props
> = P & Record<Exclude<keyof P, keyof CustomDialogProps<C>>, never>; // restricts to only known keys

/**
 * Open a dialog with the centralized dialog system.
 * See {@link DialogComponent} for more information about requirements and options
 */
export const showDialog = <
  C extends DialogComponent<any>, // dialog component
  P extends CustomDialogProps<C>, // custom props
>(
  dialogComponent: C,
  // trick to make the supplied props optional when there are no required custom props.
  // explanation: conditional type for the ...args
  // can unknown (or an empty object) be assigned to the dialogComponent custom props? meaning: are there no required custom props?
  ...args: unknown extends CustomDialogProps<C>
    ? // Yes: The component takes no required props, so the dialogProps arg is optional.
      [dialogProps?: SuppliedDialogProps<C, P>]
    : // No: The component has required props, so the argument is required.
      [dialogProps: SuppliedDialogProps<C, P>]
): DialogAction<P> => {
  const [dialogProps] = args;
  return {
    type: 'SHOW_DIALOG',
    dialogComponent,
    dialogProps: (dialogProps ?? {}) as P,
  };
};

/**
 * Can be called to flag that the currently visible dialog is currently "saving".
 * This is automatically called if your dialog has a prop called `onSubmit`. See {@link ./DialogHandler.tsx}
 */
export const startSavingDialog = (): DialogAction<unknown> => ({
  type: 'START_SAVING_DIALOG',
});

/**
 * Pass an error message to be displayed in the dialog (to be implemented in the dialog itself)
 */
export const showDialogError = (error: string): DialogAction<unknown> => ({
  type: 'SHOW_DIALOG_ERROR_MESSAGE',
  error,
});

/**
 * Close the currently visible dialog
 */
export const closeDialog = (): DialogAction<unknown> => ({
  type: 'CLOSE_DIALOG',
});
